import { CachedItemType } from "@/types/caching/cachedItemType"
import Vue from "vue"

export default class DataCache<T extends CachedObject> {
	private cache: T[] = []
	private cacheMap: { [key: string]: T } = {}

	public readonly cacheKey: CachedItemType
	private readonly fetchMethod: () => Promise<T[]>
	private readonly buildMap: boolean = false
	private readonly expireAfterMins: number = 180
	private fetchPromise: Promise<T[]> | null = null
	private lastFetchedAt: Date | null = null
	private pendingRefresh: boolean = true
	private _timesFetched: number = 0

	constructor (cacheKey: CachedItemType, fetchMethod: () => Promise<T[]>, expireAfterMins: number = 180, buildMap: boolean = false) {
		this.cacheKey = cacheKey
		this.fetchMethod = fetchMethod
		this.expireAfterMins = expireAfterMins
		this.buildMap = buildMap
	}

	public get timesFetched (): number {
		return this._timesFetched
	}

	public get isFetching (): boolean {
		return this.fetchPromise !== null
	}

	public get hasFetchedData (): boolean {
		return this.timesFetched > 0
	}

	public getById (id: string): T | undefined {
		if (this.logErrorIfNeverFetched("ID: " + id).hasNeverFetched) {
			return undefined
		}

		return this.buildMap
			? this.cacheMap[id]
			: this.cache.find(item => item.id === id)
	}

	public get items (): T[] {
		this.logErrorIfNeverFetched()
		return this.cache
	}

	public get itemMap (): { [key: string]: T } {
		this.logErrorIfNeverFetched()
		if (!this.buildMap) throw new Error("Cannot get map when buildMap is false. Cache: " + this.cacheKey)
		return this.cacheMap
	}

	public async fetchIfNeeded (): Promise <T[]> {
		if (!this.fetchPromise && (this.pendingRefresh || this.isCacheExpired())) {
			console.log("Fetching data for cache: " + this.cacheKey)
			await this.fetch()
		} else if (this.fetchPromise) {
			await this.fetchPromise
		}

		return this.cache
	}

	public async fetchAll (): Promise <T[]> {
		await this.fetch()
		return this.cache
	}

	public setPendingRefresh (value: boolean): void{
		this.pendingRefresh = value
	}

	private async fetch () {
		let promiseResolve: any = null
		try {
			this.lastFetchedAt = new Date()
			Vue.set(this, "fetchPromise", new Promise((resolve, reject) => promiseResolve = resolve))
			const dataPromise = this.fetchMethod()
			this.cache = await dataPromise
			Vue.set(this, "_timesFetched", this.timesFetched + 1)

			if (this.buildMap) {
				this.cacheMap = this.cache.reduce((acc, item) => {
					acc[item.id as string] = item
					return acc
				}, {} as { [key: string]: T })
			}

			this.pendingRefresh = false
		} catch (e) {
			this.pendingRefresh = true
			throw new Error("Failed to fetch data for cache: " + this.cacheKey + ". Error: " + e!.toString())
		} finally {
			if (promiseResolve !== null) promiseResolve(this.cache)
			Vue.set(this, "fetchPromise", null)
		}
	}

	private logErrorIfNeverFetched (additionalInfo: string = ""): { hasNeverFetched: boolean } {
		if (!this.hasFetchedData) {
			this.logGetBeforeFetchError(additionalInfo)
		}

		return { hasNeverFetched: !this.hasFetchedData }
	}

	private logGetBeforeFetchError (additionalInfo: string): void {
		console.error(`Attempted to get data from cache before it was fetched. Cache: ${this.cacheKey}. ${additionalInfo}`)
	}

	private isCacheExpired (): boolean {
		if (!this.lastFetchedAt) return true
		const now = new Date()
		const diff = now.getTime() - this.lastFetchedAt.getTime()
		const diffMins = Math.floor(diff / 1000 / 60)
		return diffMins >= this.expireAfterMins
	}
}

export interface CachedObject {
	id?: string | null
}
